/**
 * MessagesSubPage
 * Conversation list + detail view, embedded in the People tab.
 * Extracted and adapted from InboxPage.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Capacitor } from '@capacitor/core';
import {
  listConversations,
  getConversation,
  sendReply,
  markRead,
  archiveConversation,
  startConversation,
  isSignedIn,
  Conversation,
  ConversationDetail,
} from '../services/messagingService';
import PageTemplate from './PageTemplate';
import NewMessageModal from './NewMessageModal';

interface MessagesSubPageProps {
  isActive?: boolean;
  onUnreadCountChange?: (count: number) => void;
}

const MessagesSubPage: React.FC<MessagesSubPageProps> = ({ isActive = true, onUnreadCountChange }) => {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [detail, setDetail] = useState<ConversationDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const [showNewMessage, setShowNewMessage] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [keyboardOffset, setKeyboardOffset] = useState(0);

  const loadConversations = useCallback(async () => {
    if (!isSignedIn()) return;
    setLoading(true);
    const data = await listConversations();
    setConversations(data);
    setLoading(false);
    // Update unread count
    const total = data.reduce((sum, c) => sum + c.unread_count, 0);
    onUnreadCountChange?.(total);
  }, [onUnreadCountChange]);

  useEffect(() => {
    if (isActive) {
      loadConversations();
    }
  }, [isActive, loadConversations]);

  const openConversation = useCallback(async (id: number) => {
    setSelectedId(id);
    setDetailLoading(true);
    setReplyText('');
    const data = await getConversation(id);
    setDetail(data);
    setDetailLoading(false);
    await markRead(id);
    setConversations((prev) =>
      prev.map((c) => (c.id === id ? { ...c, unread_count: 0 } : c)),
    );
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [detail?.messages]);

  // Track keyboard so input stays above it
  const isIOSNative = Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios';

  useEffect(() => {
    if (isIOSNative) {
      // Use Capacitor Keyboard plugin on iOS native
      const { Keyboard } = require('@capacitor/keyboard');
      const showListener = Keyboard.addListener('keyboardWillShow', (info: any) => {
        setKeyboardOffset(info.keyboardHeight || 300);
        setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
      });
      const hideListener = Keyboard.addListener('keyboardWillHide', () => {
        setKeyboardOffset(0);
      });
      return () => {
        showListener.then((l: any) => l.remove());
        hideListener.then((l: any) => l.remove());
      };
    } else {
      // Fallback: visualViewport for web browsers
      const vv = window.visualViewport;
      if (!vv) return;
      const onResize = () => {
        const offset = window.innerHeight - vv.height;
        setKeyboardOffset(offset > 50 ? offset : 0);
      };
      vv.addEventListener('resize', onResize);
      vv.addEventListener('scroll', onResize);
      return () => {
        vv.removeEventListener('resize', onResize);
        vv.removeEventListener('scroll', onResize);
      };
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSendReply = async () => {
    if (!selectedId || !replyText.trim() || sending) return;
    setSending(true);
    const res = await sendReply(selectedId, replyText.trim());
    setSending(false);
    if (res.success) {
      setReplyText('');
      if (textareaRef.current) textareaRef.current.style.height = 'auto';
      const data = await getConversation(selectedId);
      setDetail(data);
      loadConversations();
    }
  };

  const handleNewMessageSent = async (conversationId: number) => {
    setShowNewMessage(false);
    await loadConversations();
    openConversation(conversationId);
  };

  const formatTime = (dateStr: string) => {
    const d = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return 'just now';
    if (diffMin < 60) return `${diffMin}m`;
    const diffHr = Math.floor(diffMin / 60);
    if (diffHr < 24) return `${diffHr}h`;
    const diffDay = Math.floor(diffHr / 24);
    if (diffDay < 7) return `${diffDay}d`;
    return d.toLocaleDateString();
  };

  // ---- Swipe-to-delete ----
  const [swipeState, setSwipeState] = useState<Record<number, number>>({});
  const swipeDragging = useRef<number | null>(null);
  const swipeTouchStartX = useRef(0);
  const swipeTouchStartY = useRef(0);
  const swipeIsHorizontal = useRef<boolean | null>(null);
  const SWIPE_THRESHOLD = 80;
  const SWIPE_MAX = 120;

  const handleSwipeDelete = async (id: number) => {
    // TODO: Replace confirm with soft-delete / trash in the future
    if (!window.confirm('Delete this conversation?')) return;
    const ok = await archiveConversation(id);
    if (ok) {
      setConversations((prev) => prev.filter((c) => c.id !== id));
      if (selectedId === id) { setSelectedId(null); setDetail(null); }
    }
  };

  const onRowTouchStart = (id: number, e: React.TouchEvent) => {
    swipeDragging.current = id;
    swipeTouchStartX.current = e.touches[0].clientX;
    swipeTouchStartY.current = e.touches[0].clientY;
    swipeIsHorizontal.current = null;
  };

  const onRowTouchMove = (id: number, e: React.TouchEvent) => {
    if (swipeDragging.current !== id) return;
    const dx = e.touches[0].clientX - swipeTouchStartX.current;
    const dy = e.touches[0].clientY - swipeTouchStartY.current;
    if (swipeIsHorizontal.current === null) {
      if (Math.abs(dx) > 10 || Math.abs(dy) > 10) {
        swipeIsHorizontal.current = Math.abs(dx) > Math.abs(dy);
      }
    }
    if (!swipeIsHorizontal.current) return;
    e.preventDefault();
    if (dx > 0) return; // only left swipe
    let clamped = dx;
    if (Math.abs(clamped) > SWIPE_MAX) {
      const over = Math.abs(clamped) - SWIPE_MAX;
      clamped = -1 * (SWIPE_MAX + over * 0.2);
    }
    setSwipeState((prev) => ({ ...prev, [id]: clamped }));
  };

  const onRowTouchEnd = (id: number) => {
    if (swipeDragging.current !== id) return;
    swipeDragging.current = null;
    swipeIsHorizontal.current = null;
    const currentTx = swipeState[id] || 0;
    if (currentTx < -SWIPE_THRESHOLD) {
      handleSwipeDelete(id);
    }
    setSwipeState((prev) => ({ ...prev, [id]: 0 }));
  };

  const renderConversationRow = (c: Conversation) => {
    const tx = swipeState[c.id] || 0;
    const dragging = swipeDragging.current === c.id;
    const showDeleteBg = tx < -20;
    const deleteOpacity = Math.min(1, Math.abs(Math.min(0, tx)) / SWIPE_THRESHOLD);
    const notSwiping = Math.abs(tx) < 5;
    return (
      <div key={c.id} style={{ position: 'relative', overflow: 'hidden', borderBottom: '1px solid var(--color-border)' }}>
        {showDeleteBg && (
          <div style={{
            position: 'absolute', top: 0, right: 0, bottom: 0, width: Math.abs(tx),
            background: '#dc2626', display: 'flex', alignItems: 'center', justifyContent: 'center',
            opacity: deleteOpacity, color: '#fff', fontWeight: 600, fontSize: '14px', gap: 6,
          }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="3 6 5 6 21 6"></polyline>
              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
            </svg>
            Delete
          </div>
        )}
        <div
          style={{
            display: 'flex', alignItems: 'center', gap: '12px', padding: '14px 16px',
            cursor: 'pointer', textAlign: 'left' as const,
            background: c.unread_count > 0 ? 'var(--color-bg-secondary)' : 'var(--color-bg-primary)',
            transform: `translateX(${tx}px)`,
            transition: dragging ? 'none' : 'transform 0.3s ease-out',
          }}
          onClick={() => { if (notSwiping) openConversation(c.id); }}
          onTouchStart={(e) => onRowTouchStart(c.id, e)}
          onTouchMove={(e) => onRowTouchMove(c.id, e)}
          onTouchEnd={() => onRowTouchEnd(c.id)}
        >
          <div style={{
            width: 40, height: 40, borderRadius: '50%', background: 'var(--color-accent)',
            color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontWeight: '600', fontSize: '16px', flexShrink: 0,
          }}>
            {(c.other_user.display_name || '?')[0].toUpperCase()}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2px' }}>
              <span style={{ fontWeight: c.unread_count > 0 ? '600' : '500', fontSize: '15px' }}>
                {c.other_user.display_name || 'Unknown'}
              </span>
              <span style={{ fontSize: '12px', color: 'var(--color-text-muted)', flexShrink: 0 }}>
                {c.last_message ? formatTime(c.last_message.created_at) : ''}
              </span>
            </div>
            {c.item && (
              <div style={{ fontSize: '12px', color: 'var(--color-text-muted)', marginBottom: '2px' }}>
                Re: {c.item.name}
              </div>
            )}
            <div style={{
              fontSize: '13px',
              color: c.unread_count > 0 ? 'var(--color-text-primary)' : 'var(--color-text-muted)',
              fontWeight: c.unread_count > 0 ? '500' : '400',
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            }}>
              {c.last_message
                ? (c.last_message.is_mine ? 'You: ' : '') + c.last_message.body
                : 'No messages'}
            </div>
          </div>
          {c.unread_count > 0 && (
            <div style={{
              minWidth: 20, height: 20, borderRadius: '10px', background: 'var(--color-accent)',
              color: '#fff', fontSize: '11px', fontWeight: '700',
              display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 6px',
            }}>
              {c.unread_count}
            </div>
          )}
        </div>
      </div>
    );
  };

  if (!isSignedIn()) {
    return (
      <PageTemplate showPageHeader={false} showInputArea={false} hasGrayBackground={false} enableSmartScroll={false} className="groups-page">
        <div style={{ padding: '60px 20px', textAlign: 'center', color: 'var(--color-text-muted)' }}>
          <p style={{ fontSize: '15px', margin: 0 }}>Sign in to view messages</p>
        </div>
      </PageTemplate>
    );
  }

  // ---- Conversation Detail View ----
  if (selectedId) {
    return (
      <div
        style={{
          position: 'fixed',
          top: 'var(--unified-header-offset, 184px)',
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          flexDirection: 'column',
          background: 'var(--color-bg-primary)',
          color: 'var(--color-text-primary)',
          zIndex: 999,
        }}
      >
        {/* Detail header with back button */}
        <div
          style={{
            padding: '10px 16px',
            borderBottom: '1px solid var(--color-border)',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            background: 'var(--color-bg-secondary)',
          }}
        >
          <button
            onClick={() => { setSelectedId(null); setDetail(null); }}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--color-text-secondary)',
              cursor: 'pointer',
              padding: '4px',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6"></polyline>
            </svg>
          </button>
          <span style={{ fontWeight: '600', fontSize: 'var(--font-size-base)', flex: 1 }}>
            {detail?.conversation.other_user.display_name || 'Conversation'}
          </span>
          <button
            onClick={async () => {
              if (!selectedId) return;
              if (!window.confirm('Delete this conversation?')) return;
              const ok = await archiveConversation(selectedId);
              if (ok) {
                setSelectedId(null);
                setDetail(null);
                loadConversations();
              }
            }}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--color-text-muted)',
              cursor: 'pointer',
              padding: '4px',
              display: 'flex',
              alignItems: 'center',
            }}
            title="Delete conversation"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="3 6 5 6 21 6"></polyline>
              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
              <line x1="10" y1="11" x2="10" y2="17"></line>
              <line x1="14" y1="11" x2="14" y2="17"></line>
            </svg>
          </button>
        </div>

        {detailLoading ? (
          <div style={{ padding: '40px', textAlign: 'center', color: 'var(--color-text-muted)' }}>Loading…</div>
        ) : detail ? (
          <>
            {/* Item context bar */}
            {detail.conversation.item && (
              <div
                style={{
                  padding: '8px 16px',
                  borderBottom: '1px solid var(--color-border)',
                  background: 'var(--color-bg-secondary)',
                  fontSize: 'var(--font-size-xs)',
                  color: 'var(--color-text-muted)',
                }}
              >
                About: <strong>{detail.conversation.item.name}</strong>
              </div>
            )}
            {/* Messages */}
            <div style={{ flex: 1, overflow: 'auto', padding: '16px' }}>
              {detail.messages.map((m) => (
                <div
                  key={m.id}
                  style={{
                    display: 'flex',
                    justifyContent: m.is_mine ? 'flex-end' : 'flex-start',
                    marginBottom: '10px',
                  }}
                >
                  <div
                    style={{
                      maxWidth: m.is_mine ? '75%' : '100%',
                      width: m.is_mine ? undefined : '100%',
                      padding: '10px 14px',
                      borderRadius: m.is_mine ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                      background: m.is_mine ? 'var(--color-accent)' : 'var(--color-bg-secondary)',
                      color: m.is_mine ? '#fff' : 'var(--color-text-primary)',
                      fontSize: 'var(--font-size-sm)',
                      lineHeight: '1.4',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      textAlign: 'left' as const,
                    }}
                  >
                    {!m.is_mine && (
                      <div style={{ fontSize: 'var(--font-size-xs)', fontWeight: '600', marginBottom: '4px', opacity: 0.7 }}>
                        {m.sender_name}
                      </div>
                    )}
                    {m.body}
                    <div style={{ fontSize: 'var(--font-size-xs)', opacity: 0.6, marginTop: '4px', textAlign: 'right' }}>
                      {formatTime(m.created_at)}
                    </div>
                  </div>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>
            {/* Reply input - positioned above keyboard when visible */}
            <div
              style={{
                padding: '8px 16px',
                paddingBottom: keyboardOffset > 0
                  ? `${8 + keyboardOffset}px`
                  : 'calc(8px + 76px)',
                borderTop: '1px solid var(--color-border)',
                background: 'var(--color-bg-secondary)',
                display: 'flex',
                gap: '8px',
                alignItems: 'flex-end',
                transition: 'padding-bottom 0.1s ease-out',
              }}
            >
              <textarea
                ref={textareaRef}
                value={replyText}
                onChange={(e) => {
                  setReplyText(e.target.value);
                  const ta = e.target;
                  ta.style.height = 'auto';
                  ta.style.height = Math.min(ta.scrollHeight, 120) + 'px';
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey && !isIOSNative) {
                    e.preventDefault();
                    handleSendReply();
                  }
                }}
                placeholder="Type a message…"
                rows={1}
                style={{
                  flex: 1,
                  padding: '10px 14px',
                  borderRadius: '20px',
                  border: '1px solid var(--color-border)',
                  background: 'var(--color-bg-primary)',
                  color: 'var(--color-text-primary)',
                  fontSize: 'var(--font-size-base)',
                  lineHeight: '1.4',
                  outline: 'none',
                  resize: 'none',
                  fontFamily: 'inherit',
                  maxHeight: '120px',
                  overflow: 'auto',
                  WebkitAppearance: 'none' as any,
                }}
              />
              <button
                onClick={handleSendReply}
                disabled={sending || !replyText.trim()}
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: '50%',
                  border: 'none',
                  background: replyText.trim() ? 'var(--color-accent)' : 'var(--color-bg-tertiary)',
                  color: replyText.trim() ? '#fff' : 'var(--color-text-muted)',
                  cursor: replyText.trim() ? 'pointer' : 'default',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="22" y1="2" x2="11" y2="13"></line>
                  <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
                </svg>
              </button>
            </div>
          </>
        ) : (
          <div style={{ padding: '40px', textAlign: 'center', color: 'var(--color-text-muted)' }}>
            Failed to load conversation
          </div>
        )}
      </div>
    );
  }

  // ---- Conversation List View ----
  return (
    <PageTemplate showPageHeader={false} showInputArea={false} hasGrayBackground={false} enableSmartScroll={false} className="groups-page">
      {loading ? (
        <div style={{ padding: '40px', textAlign: 'center', color: 'var(--color-text-muted)' }}>Loading…</div>
      ) : conversations.length === 0 ? (
        <div style={{ padding: '60px 20px', textAlign: 'center', color: 'var(--color-text-muted)' }}>
          <div style={{ fontSize: '40px', marginBottom: '12px' }}>💬</div>
          <p style={{ fontSize: '15px', margin: 0 }}>No messages yet</p>
          <p style={{ fontSize: '13px', marginTop: '8px', color: 'var(--color-text-muted)' }}>
            Tap + to start a conversation
          </p>
        </div>
      ) : (
        conversations.map(renderConversationRow)
      )}

      {/* Floating New Message button */}
      <button
        onClick={() => setShowNewMessage(true)}
        style={{
          position: 'fixed',
          bottom: '80px',
          right: '20px',
          width: 56,
          height: 56,
          borderRadius: '50%',
          border: 'none',
          background: 'var(--color-accent)',
          color: '#fff',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
          zIndex: 100,
        }}
        title="New message"
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
          <line x1="12" y1="8" x2="12" y2="14"></line>
          <line x1="9" y1="11" x2="15" y2="11"></line>
        </svg>
      </button>

      {/* New Message Modal */}
      {showNewMessage && (
        <NewMessageModal
          onClose={() => setShowNewMessage(false)}
          onMessageSent={handleNewMessageSent}
        />
      )}
    </PageTemplate>
  );
};

export default MessagesSubPage;
